import React, { useState } from "react";
import {
  Upload,
  X,
  CheckCircle,
  Loader2,
  AlertCircle,
  FileImage,
} from "lucide-react";
import axios from "axios";
import { apiUrl } from "../../constants";

const ImageUpload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState("");
  const [error, setError] = useState("");

  const handleFileSelect = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    // Validate file type
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!validTypes.includes(file.type)) {
      setError("Please select a valid image file (JPEG, PNG, GIF, or WebP)");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError("File size should not exceed 5MB");
      return;
    }

    setError("");
    setSelectedFile(file);
    setUploadSuccess(false);
    setUploadedUrl("");

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError("Please select an image first");
      return;
    }

    setUploading(true);
    setError("");

    try {
      // Step 1: Get presigned URL from your backend
      const imageType = selectedFile.type.split("/")[1];
      const response = await axios.post(`${apiUrl}/uploadimages`, {
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ type: imageType }),
      });
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Failed to get upload URL");
      }

      // Step 2: Upload image to S3 using presigned URL
      const uploadResponse = await fetch(data.url, {
        method: "PUT",
        body: selectedFile,
        headers: {
          "Content-Type": selectedFile.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload image to S3");
      }

      // Step 3: Get the public URL (remove query parameters)
      const publicUrl = data.url.split("?")[0];

      setUploadedUrl(publicUrl);
      setUploadSuccess(true);

      console.log("Image uploaded successfully:", publicUrl);
      console.log("File name:", data.fileName);
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.message || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setPreview(null);
    setUploadSuccess(false);
    setUploadedUrl("");
    setError("");
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(uploadedUrl);
    alert("URL copied to clipboard!");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-6 flex items-center justify-center">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white bg-opacity-20 rounded-xl">
                <FileImage className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Image Upload</h1>
                <p className="text-blue-100 text-sm mt-1">
                  Upload your images to AWS S3
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Upload Area */}
            {!preview ? (
              <label className="block">
                <div className="border-4 border-dashed border-gray-300 rounded-2xl p-12 text-center hover:border-blue-500 hover:bg-blue-50 transition-all cursor-pointer">
                  <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg font-semibold text-gray-700 mb-2">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-sm text-gray-500">
                    PNG, JPG, GIF or WebP (Max 5MB)
                  </p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>
            ) : (
              <div className="space-y-6">
                {/* Preview */}
                <div className="relative">
                  <img
                    src={preview}
                    alt="Preview"
                    className="w-full h-80 object-contain bg-gray-50 rounded-xl border-2 border-gray-200"
                  />
                  {!uploadSuccess && (
                    <button
                      onClick={handleReset}
                      className="absolute top-4 right-4 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>

                {/* File Info */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold">File:</span>{" "}
                    {selectedFile?.name}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    <span className="font-semibold">Size:</span>{" "}
                    {(selectedFile?.size / 1024).toFixed(2)} KB
                  </p>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                )}

                {/* Success Message */}
                {uploadSuccess && (
                  <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                      <p className="text-green-700 font-semibold">
                        Image uploaded successfully!
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <p className="text-xs text-gray-500 mb-1">Image URL:</p>
                      <div className="flex items-center gap-2">
                        <code className="text-sm text-gray-700 flex-1 truncate">
                          {uploadedUrl}
                        </code>
                        <button
                          onClick={handleCopyUrl}
                          className="px-3 py-1 bg-green-500 text-white text-xs rounded-lg hover:bg-green-600 transition-colors flex-shrink-0"
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  {!uploadSuccess ? (
                    <>
                      <button
                        onClick={handleUpload}
                        disabled={uploading}
                        className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-4 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-5 h-5" />
                            Upload to S3
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleReset}
                        disabled={uploading}
                        className="px-6 bg-gray-100 text-gray-700 py-4 rounded-xl font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleReset}
                      className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-4 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all"
                    >
                      Upload Another Image
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className="bg-white rounded-xl p-4 shadow-md">
            <div className="text-blue-500 font-semibold text-sm mb-1">
              Max File Size
            </div>
            <div className="text-2xl font-bold text-gray-800">5 MB</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-md">
            <div className="text-purple-500 font-semibold text-sm mb-1">
              Supported Formats
            </div>
            <div className="text-lg font-bold text-gray-800">
              JPG, PNG, GIF, WebP
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-md">
            <div className="text-green-500 font-semibold text-sm mb-1">
              Storage
            </div>
            <div className="text-2xl font-bold text-gray-800">AWS S3</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;
